import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import { assets, timeline } from "../../data/sep26Data";
import { EASE_OUT } from "./hooks";

const Lightbox = ({ title, urls, onClose }) => {
  const [i, setI] = useState(0);
  const go = (d) => setI((v) => (v + d + urls.length) % urls.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="s26-historia-lightbox"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4 p-4"
      style={{ background: "rgba(7,10,28,.94)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <button type="button" aria-label="Cerrar" className="absolute right-4 top-4 grid h-11 w-11 place-items-center border" style={{ borderColor: "var(--line)" }} onClick={onClose}>
        <X size={20} />
      </button>
      <img
        src={urls[i]}
        alt={`${title}, foto ${i + 1} de ${urls.length}`}
        className="max-h-[78vh] max-w-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <button type="button" aria-label="Anterior" className="grid h-11 w-11 place-items-center border" style={{ borderColor: "var(--line)" }} onClick={() => go(-1)}>
          <ChevronLeft size={20} />
        </button>
        <span className="label">{i + 1} / {urls.length}</span>
        <button type="button" aria-label="Siguiente" className="grid h-11 w-11 place-items-center border" style={{ borderColor: "var(--line)" }} onClick={() => go(1)}>
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );
};

export const Historia26 = () => {
  const reduce = useReducedMotion();
  const listRef = useRef(null);
  const [gallery, setGallery] = useState(null);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 70%", "end 60%"] });
  const line = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="historia" data-testid="s26-historia" className="section-pad" style={{ background: "var(--navy-deep)" }}>
      <div className="wrap grid gap-14 lg:grid-cols-[minmax(260px,0.8fr)_1.4fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <h2 className="display text-[clamp(48px,7vw,104px)]">
            Nuestra <span style={{ color: "var(--gold)" }}>historia</span>
          </h2>
          <p className="serif mt-5 max-w-[26ch] text-[clamp(20px,2vw,26px)]" style={{ color: "var(--muted)" }}>
            Más de una década en la cancha, con la misma gente.
          </p>
          <img src={assets.crest} alt="" aria-hidden="true" className="mt-10 hidden w-36 opacity-90 lg:block" loading="lazy" />
        </div>

        <div ref={listRef} className="relative pl-8 sm:pl-12">
          <div className="absolute bottom-0 left-[7px] top-2 w-px sm:left-[11px]" style={{ background: "var(--line)" }} aria-hidden="true" />
          <motion.div
            className="absolute left-[7px] top-2 bottom-0 w-px origin-top sm:left-[11px]"
            style={{ background: "var(--gold)", scaleY: reduce ? 1 : line }}
            aria-hidden="true"
          />

          <ol className="space-y-14">
            {timeline.map((h, i) => (
              <motion.li
                key={`${h.year}-${h.title}`}
                className="relative"
                data-testid={`s26-hito-${i + 1}`}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: EASE_OUT }}
              >
                <span
                  className="absolute -left-8 top-3 h-[15px] w-[15px] rounded-full border-2 sm:-left-12 sm:h-[23px] sm:w-[23px]"
                  style={{ background: "var(--ink)", borderColor: "var(--gold)" }}
                  aria-hidden="true"
                />
                <span
                  className="display block text-[clamp(64px,9vw,130px)]"
                  style={{ color: "transparent", WebkitTextStroke: "1.5px rgba(224,176,74,.7)" }}
                >
                  {h.year}
                </span>
                <h3 className="display -mt-1 text-[clamp(28px,3vw,42px)]">{h.title}</h3>
                <p className="mt-3 max-w-[52ch]" style={{ color: "var(--muted)" }}>{h.description}</p>
                {h.galleryImages && (
                  <button
                    type="button"
                    className="btn btn--ghost mt-5"
                    data-testid="s26-historia-galeria"
                    onClick={() => setGallery({ title: h.title, urls: h.galleryImages })}
                  >
                    <Images size={18} aria-hidden="true" /> {h.galleryLabel}
                  </button>
                )}
              </motion.li>
            ))}
          </ol>
        </div>
      </div>

      <AnimatePresence>
        {gallery && <Lightbox title={gallery.title} urls={gallery.urls} onClose={() => setGallery(null)} />}
      </AnimatePresence>
    </section>
  );
};
